/**
 * O espelho da ordenação de listas (TUT-204c).
 *
 * A lista canônica vive na API (`apps/api/src/domain/list-sorting.ts`); aqui
 * ela existe porque o web é pacote separado e não importa do servidor — a mesma
 * política dos outros tipos-espelho que a TUT-182 preservou de propósito.
 *
 * **Espelho sem trava diverge.** É a família do descritor de status, onde o web
 * usa chave minúscula e a API maiúscula, e ninguém percebe até a tela pedir
 * algo que o servidor recusa com 400. Por isso `list-sorting.test.ts` lê os
 * DOIS arquivos e reprova se as listas deixarem de bater.
 *
 * **O que mora AQUI e não lá:** o ciclo de três posições do cabeçalho e o
 * `aria-sort`. Os dois são decisão de interface — o servidor recebe coluna e
 * direção já escolhidas e não tem cabeçalho nenhum. A primeira versão desta
 * fatia os tinha na API, e o `exactOptionalPropertyTypes` reprovou o
 * `{ sort: undefined }` do terceiro clique; a reprovação apontou o lugar certo.
 */

export const LIST_SORT_DIRECTIONS = ["asc", "desc"] as const;
export type ListSortDirection = (typeof LIST_SORT_DIRECTIONS)[number];

export const LIST_SORT_COLUMNS = {
  experts: ["nome", "vinculados", "situacao"],
  counsels: ["nome", "oab", "vinculados", "situacao"],
  organizations: ["nome", "tipo", "documento", "cidade"],
  people: ["pessoa", "situacao"],
  rentalCompanies: ["nome", "situacao"],
  // TUT-589 — fornecedores; "tipo" ordena pelo rótulo (Física/Jurídica).
  suppliers: ["nome", "tipo", "documento", "situacao"],
  deadlines: [
    "prazo",
    "processo",
    "reclamante",
    "responsavel",
    "vencimento",
    "status",
  ],
  expertises: ["processo", "reclamante", "data-e-hora", "especialidade", "status"],
  // Família C (trilhas por data) — os porquês de cada inclusão e exclusão
  // moram no arquivo canônico da API; aqui só o espelho, que a trava compara.
  auditEvents: ["quando", "responsavel"],
  notificationDeliveries: [
    "quando",
    "evento",
    "canal",
    "destinatario",
    "status",
    "erro",
  ],
  documents: ["titulo", "processo", "reclamante", "atualizado", "status", "versao"],
  // Família D — os porquês (inclusive as exclusões de Viagens: `km` e
  // `transporte` são células compostas, `situacao` é derivada no cliente)
  // moram no arquivo canônico da API.
  vehicleTrips: [
    "saida",
    "modalidade",
    "quem",
    "destino",
    "valor",
    "reembolso",
    "pagador",
  ],
  specialWorks: ["trabalho", "cliente", "valor", "status", "atualizado"],
  proposals: [
    "numero",
    "cliente",
    "solicitante",
    "descricao",
    "modalidade",
    "status",
    "atualizado",
  ],
  users: ["nome", "email", "desde"],
  processSubjects: ["assunto", "cnj", "origem", "situacao"],
  legalBriefs: ["processo", "situacao", "prazo", "responsavel"],
  accountsPayable: ["representante", "processo", "vencimento", "valor", "situacao"],
  accountsReceivable: ["cliente", "processo", "marco", "rota", "vencimento", "valor", "situacao"],
  // TUT-537 — Defesas; os porquês moram no arquivo canônico da API.
  defesas: ["documento", "processo", "cliente", "resultado", "risco", "ficha"],
} as const;

export type ListaOrdenavel = keyof typeof LIST_SORT_COLUMNS;
export type ColunaDe<L extends ListaOrdenavel> =
  (typeof LIST_SORT_COLUMNS)[L][number];

export function isColunaDaLista<L extends ListaOrdenavel>(
  lista: L,
  valor: unknown,
): valor is ColunaDe<L> {
  return (
    typeof valor === "string" &&
    (LIST_SORT_COLUMNS[lista] as readonly string[]).includes(valor)
  );
}

export function isDirecaoDeOrdenacao(
  valor: unknown,
): valor is ListSortDirection {
  return valor === "asc" || valor === "desc";
}

/**
 * TUT-204c — as ordens operacionais das situações, espelhando a API.
 *
 * **Prazos ordena pela situação APRESENTADA.** `Vencido` não é valor gravado:
 * o servidor o deriva de `PENDING` com vencimento no passado. Aqui o espelho de
 * demonstração já recebe a chave derivada em `status.key`, então ele lê direto
 * — e é justamente por isso que as duas listas precisam bater: se a API
 * ordenasse por três posições e a demo por quatro, a e2e (que roda na demo)
 * provaria um produto que o servidor não entrega.
 *
 * **Perícias** usa as chaves em MAIÚSCULAS, como o servidor; o descritor da
 * tela é minúsculo, e a conversão acontece no acessor. É a assimetria que o
 * `AGENTS.md` já registra entre os descritores de status dos dois lados.
 */
export const DEADLINE_STATUS_SORT_ORDER = [
  "overdue",
  "pending",
  "completed",
  "cancelled",
] as const;

export const EXPERTISE_STATUS_SORT_ORDER = [
  "PENDING_REPORT",
  "TO_SCHEDULE",
  "SCHEDULED",
  // TUT-527 — a que vai acontecer e ainda não tem data, logo depois da agendada.
  "AWAITING_COURT_DATE",
  "COMPLETED",
  "RESCHEDULED",
  "FRUSTRATED",
  "CANCELLED",
] as const;

// TUT-204c (família C) — espelhos das ordens do histórico de envios; os
// porquês (failed é a única situação com ação na tela; canal agrupa pelo
// rótulo que se lê) moram no arquivo canônico da API.
export const NOTIFICATION_DELIVERY_STATUS_SORT_ORDER = [
  "failed",
  "pending",
  "sent",
  "reprocessed",
] as const;

export const NOTIFICATION_CHANNEL_SORT_ORDER = [
  "email",
  "internal",
  "telegram",
] as const;

// TUT-204c (família D) — espelhos das ordens novas; os porquês (a prioridade
// de foco da própria tela nos trabalhos, o rótulo lido nas viagens, a
// hierarquia de alcance nos papéis) moram no arquivo canônico da API.
export const SPECIAL_WORK_STATUS_SORT_ORDER = [
  "REVIEW",
  "SENT",
  "ACCEPTED",
  "IN_PROGRESS",
  "DRAFT",
  "COMPLETED",
  "DECLINED",
  "CANCELLED",
] as const;

// TUT-204f — a ordem operacional das peças; o porquê mora no arquivo
// canônico da API.
export const LEGAL_BRIEF_STATUS_SORT_ORDER = [
  // TUT-291a — estado novo da impugnação (aguarda o laudo do assistente).
  "AWAITING_EXPERT_REPORT",
  "DRAFTING",
  "IN_REVIEW",
  "AWAITING_DELIVERY",
  "DELIVERED",
  "NOT_DELIVERED",
  "CANCELLED",
] as const;

export const VEHICLE_TRIP_MODE_SORT_ORDER = [
  "AIR",
  "RENTED",
  "RIDE_HAILING",
  "FLEET",
  "BUS",
  "OWN",
  "TAXI",
] as const;

export const VEHICLE_TRIP_PAYER_SORT_ORDER = [
  "BILL_CLIENT",
  "OFFICE",
  "REIMBURSE_PERSON",
] as const;

/** A posição da situação na ordem operacional; desconhecida vai para o fim. */
export function posicaoNaOrdem(
  ordem: readonly string[],
  situacao: string,
): number {
  const posicao = ordem.indexOf(situacao);
  return posicao === -1 ? ordem.length : posicao;
}

/** O que um acessor de ordenação pode devolver. */
export type ValorOrdenavel =
  | string
  | number
  | boolean
  | Date
  | null
  | undefined;

/**
 * A comparação de dois valores — o espelho de `compararValores` da API.
 *
 * **Esta é a TERCEIRA implementação da mesma regra, e ela é inevitável.** A
 * primeira é o SQL do PostgreSQL, a segunda é o adapter de memória, e esta
 * existe porque o **modo demonstração roda sem servidor**: a e2e compila o web
 * com `VITE_API_MODE=demo` e serve por `vite preview`, sem API e sem banco. Sem
 * ela, clicar num cabeçalho na demo não faria nada — e a e2e mediria a
 * ausência.
 *
 * O que não pode é divergir. As três regras são as mesmas, e os casos que as
 * provam estão escritos igual nos dois lados (`test/list-sorting.spec.ts` na
 * API, `list-sorting.test.ts` aqui): nulo por último nas DUAS direções, pt-BR
 * com `numeric`, e o sinal aplicado ao valor e nunca ao empate.
 */
export function compararValores(
  a: ValorOrdenavel,
  b: ValorOrdenavel,
  direcao: ListSortDirection | undefined,
): number {
  const vazioA = a === null || a === undefined || a === "";
  const vazioB = b === null || b === undefined || b === "";
  if (vazioA && vazioB) return 0;
  if (vazioA) return 1;
  if (vazioB) return -1;

  const sinal = direcao === "desc" ? -1 : 1;

  if (a instanceof Date && b instanceof Date) {
    return sinal * (a.getTime() - b.getTime());
  }
  if (typeof a === "boolean" && typeof b === "boolean") {
    return sinal * (Number(a) - Number(b));
  }
  if (typeof a === "number" && typeof b === "number") {
    return sinal * (a - b);
  }
  return (
    sinal *
    String(a).localeCompare(String(b), "pt-BR", {
      numeric: true,
      sensitivity: "base",
    })
  );
}

/** O comparador de uma lista inteira — espelho de `ordenarEmMemoria` da API. */
export function ordenarLista<T>(
  itens: readonly T[],
  opcoes: {
    coluna: string | undefined;
    direcao: ListSortDirection | undefined;
    acessores: Readonly<Record<string, (item: T) => ValorOrdenavel>>;
    desempate: (item: T) => string;
    padrao?: (a: T, b: T) => number;
  },
): T[] {
  const acessor = opcoes.coluna ? opcoes.acessores[opcoes.coluna] : undefined;
  return [...itens].sort((a, b) => {
    const principal = acessor
      ? compararValores(acessor(a), acessor(b), opcoes.direcao)
      : (opcoes.padrao?.(a, b) ?? 0);
    if (principal !== 0) return principal;
    return opcoes
      .desempate(a)
      .localeCompare(opcoes.desempate(b), "pt-BR", { numeric: true });
  });
}

/** O estado de ordenação que viaja na URL. */
export interface OrdenacaoDaLista {
  sort?: string | undefined;
  dir?: ListSortDirection | undefined;
}

/**
 * O próximo estado do cabeçalho clicado — TRÊS posições.
 *
 * Ascendente, descendente e **sem ordenação**. A terceira é a que costuma
 * faltar: voltar ao padrão do servidor tem de ser alcançável pelo mesmo clique,
 * senão a pessoa fica presa na ordem que escolheu por engano.
 */
export function proximaOrdenacaoDaLista(
  atual: OrdenacaoDaLista,
  coluna: string,
): OrdenacaoDaLista {
  if (atual.sort !== coluna) return { sort: coluna, dir: "asc" };
  if (atual.dir === "asc") return { sort: coluna, dir: "desc" };
  return { sort: undefined, dir: undefined };
}

/**
 * O `aria-sort` que o cabeçalho anuncia — contrato de acessibilidade.
 *
 * **Esta função tem chamador de verdade, e isso é a correção de um achado.** A
 * `ariaSortDaColuna` da TUT-204a nasceu igual e ficou **código morto em
 * produção**: o `SortableHeader` recalculava a mesma lógica inline, e só o
 * teste chamava o helper. A medição de 22/08 registrou isso, e a 204c conserta
 * a causa em vez do sintoma — o `SortableHeader` passou a chamar esta função,
 * então as duas lógicas viraram uma.
 */
export function ariaSortDaColunaDaLista(
  atual: {
    sort?: string | null | undefined;
    dir?: ListSortDirection | undefined;
  },
  coluna: string,
): "ascending" | "descending" | "none" {
  if (atual.sort !== coluna) return "none";
  return atual.dir === "desc" ? "descending" : "ascending";
}

/**
 * TUT-204e — espelho de `apps/api/src/domain/list-search.ts` (`contemTermo`),
 * para o adapter demo filtrar com a MESMA semântica do servidor: sem caixa
 * ("Ana" acha "ana"), sem dobra de acentos ("Joao" NÃO acha "João") — é o
 * comportamento do `ilike`, e divergir aqui seria a divergência entre
 * adapters que a família A da 204c pegou no espelho de Clientes.
 */
export function contemTermo(
  termo: string,
  campos: ReadonlyArray<string | null | undefined>,
): boolean {
  const agulha = termo.toLocaleLowerCase();
  return campos.some(
    (campo) => campo != null && campo.toLocaleLowerCase().includes(agulha),
  );
}
